import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { BUSINESS } from "@/lib/business-info";

// Authentic Empire Jamaica Fusion storefront at 385 Main Street, Poughkeepsie.
const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_caribbean-premium/artifacts/uowmbhf2_24B584D4-D59C-4E4E-97AC-8CB525C35052.PNG";

const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-charcoal"
            aria-label="Hero"
        >
            {/* Parallax background image */}
            <motion.div
                style={{ y }}
                aria-hidden
                className="absolute inset-0 z-0 will-change-transform"
            >
                {/* Two responsive bg layers — different focal points for mobile vs desktop
                    so the chandelier + palm windows remain visible on phones without
                    cutting into the headline. */}
                <div
                    className="absolute inset-0 bg-cover bg-no-repeat md:hidden"
                    style={{
                        backgroundImage: `url(${HERO_IMG})`,
                        filter: "contrast(1.06) saturate(1.06) brightness(0.96)",
                        backgroundPosition: "82% 42%",
                    }}
                />
                <div
                    className="absolute inset-0 bg-cover bg-no-repeat hidden md:block"
                    style={{
                        backgroundImage: `url(${HERO_IMG})`,
                        filter: "contrast(1.06) saturate(1.06) brightness(0.96)",
                        backgroundPosition: "88% 45%",
                    }}
                />
                {/* Strong left dark wash → soft right reveal */}
                <div className="absolute inset-0 bg-charcoal/30" />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to right, rgba(18,18,18,0.94) 0%, rgba(18,18,18,0.85) 28%, rgba(18,18,18,0.55) 55%, rgba(18,18,18,0.18) 85%, rgba(18,18,18,0.05) 100%)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-charcoal/55 via-transparent to-charcoal/90" />
                {/* Focused darkening directly behind the headline column */}
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_22%_50%,rgba(0,0,0,0.45),rgba(0,0,0,0)_70%)]" />
            </motion.div>

            {/* Content */}
            <motion.div
                style={{ opacity }}
                className="relative z-10 empire-container min-h-[100svh] flex flex-col justify-center pt-28 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="empire-eyebrow"
                    data-testid="hero-eyebrow"
                >
                    <span className="inline-block h-px w-8 bg-gold align-middle mr-3" />
                    {BUSINESS.tagline} • {BUSINESS.location}
                </motion.p>

                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="hero-headline"
                    className="mt-8 max-w-5xl font-serif text-cream text-[3rem] leading-[1] tracking-[-0.01em] md:text-[5rem] lg:text-[6.4rem]"
                >
                    Where <em className="italic font-normal text-gold">Culture</em>,
                    Flavor &<br className="hidden md:block" /> Community Meet.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="hero-subhead"
                    className="mt-7 max-w-[34rem] md:max-w-[36rem] text-cream/80 text-[15px] md:text-[17px] font-light leading-[1.7]"
                >
                    Authentic Jamaican cuisine, family recipes, and Caribbean
                    hospitality served on Main Street in Poughkeepsie.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                >
                    <button
                        type="button"
                        onClick={() => scrollTo("catering-inquiry")}
                        data-testid="hero-cta-book-catering"
                        className="empire-btn-primary"
                    >
                        Book Catering
                        <ArrowRight size={14} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollTo("private-events")}
                        data-testid="hero-cta-plan-event"
                        className="empire-btn-secondary"
                    >
                        Plan Private Event
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.4, delay: 1, ease: "easeOut" }}
                    className="hidden md:flex items-center gap-4 mt-20 text-cream/60 text-[11px] uppercase tracking-[0.28em]"
                >
                    <span className="h-px w-12 bg-cream/30" />
                    385 Main Street
                    <span className="text-gold">·</span>
                    Poughkeepsie, NY
                    <span className="text-gold">·</span>
                    Catering & Private Events
                </motion.div>
            </motion.div>

            {/* scroll indicator */}
            <div
                aria-hidden
                className="absolute z-10 bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 text-cream/40 text-[10px] uppercase tracking-[0.32em]"
            >
                <span>Scroll</span>
                <span className="block h-10 w-px bg-gradient-to-b from-gold/70 to-transparent" />
            </div>
        </section>
    );
}
